import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutGrid, Users, GraduationCap, Layers, Upload, BarChart3, Settings, LogOut, Menu, X } from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/flashcards/admin", label: "Overview", icon: LayoutGrid, end: true },
  { to: "/flashcards/admin/students", label: "Students", icon: Users },
  { to: "/flashcards/admin/courses", label: "Courses", icon: GraduationCap },
  { to: "/flashcards/admin/flashcards", label: "Flashcards", icon: Layers },
  { to: "/flashcards/admin/imports", label: "Imports", icon: Upload },
  { to: "/flashcards/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/flashcards/admin/settings", label: "Settings", icon: Settings },
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/flashcards");
  };

  const NavItems = () => (
    <>
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
          data-testid={`admin-nav-${n.label.toLowerCase()}`}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-slate-900 text-white font-medium" : "text-slate-600 hover:bg-slate-100"}`}>
          <n.icon className="h-4 w-4" /> {n.label}
        </NavLink>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden lg:flex flex-col w-60 shrink-0 bg-white border-r border-slate-200 fixed inset-y-0">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-slate-200">
          <img src="/science-sorted-logo.png" alt="Science Sorted" className="h-11 w-auto" />
          <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400 self-center">Admin</span>
        </div>
        <nav className="flex-1 p-3 space-y-1"><NavItems /></nav>
        <div className="p-3 border-t border-slate-200">
          <div className="px-3 py-2 text-xs text-slate-500 truncate">{user?.email}</div>
          <button onClick={doLogout} data-testid="admin-logout-button"
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-slate-600 hover:bg-slate-100 transition-colors">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img src="/science-sorted-logo.png" alt="Science Sorted" className="h-9 w-auto" />
          <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Admin</span>
        </div>
        <button onClick={() => setOpen(!open)} data-testid="admin-menu-toggle" className="text-slate-700">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 top-14 z-40 bg-white p-3 space-y-1">
          <NavItems />
          <button onClick={doLogout} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-slate-600 hover:bg-slate-100">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      )}

      <main className="flex-1 lg:ml-60 pt-14 lg:pt-0 min-w-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
